/**
 * 图层辅助操作：锁定 / 重命名 id。
 *
 * 锁定状态属于**编辑器私有**，写 `data-editor-locked` 且导出时必被剥除（见 io/serialize.ts）；
 * 因此「把锁定状态带出去」只能靠工程文件（存路径，见 io/project.ts）。
 *
 * 语义约定（简单且可预期）：
 *  · 锁定 → 画布上**不可点选/不可拖动**（点上去等于点空白）
 *  · 图层列表**仍可选中**（否则没法解锁），也能改 id / 删除
 */
import { nodeAt, pathOf } from '../dom';
import type { Command } from '../history';
import { attrPatch, type Patch } from '../patch';

export const LOCK_ATTR = 'data-editor-locked';

export const isLocked = (el: Element): boolean => el.getAttribute(LOCK_ATTR) === '1';

export function setLocked(root: Element, els: Iterable<Element>, locked: boolean): Command {
  const patches: Patch[] = [];
  for (const el of els) {
    const path = pathOf(el, root);
    if (!path) continue;
    const before = el.getAttribute(LOCK_ATTR);
    const patch = attrPatch(path, LOCK_ATTR, before, locked ? '1' : null);
    if (patch) patches.push(patch);
  }
  return { label: locked ? '锁定' : '解锁', patches };
}

/**
 * **仅导入时用**：把锁定状态直接写回 DOM（不走历史）。
 * 理由：锁定是编辑器私有状态、不是文档内容 —— 导入工程后文应当仍是"未修改"（pristine），
 * 否则「导出 → 导入 → 再导出字节一致」和"打开即未改动"两条都会破。
 */
export function restoreLockedState(root: Element, paths: readonly (readonly number[])[]): number {
  // ★ 必须用 nodeAt（children 索引语义）；若误用 childNodes，文档里的缩进空白节点会让路径整体错位
  const els = paths.map((path) => nodeAt(root, path)).filter((el): el is Element => el !== null && el !== root);
  for (const el of els) el.setAttribute(LOCK_ATTR, '1');
  return els.length;
}

/** 文档序的锁定元素路径（工程文件用；导出时属性被剥，所以必须另存） */
export function collectLockedPaths(root: Element): number[][] {
  const out: number[][] = [];
  const walk = (el: Element): void => {
    for (const child of Array.from(el.children)) {
      if (isLocked(child)) {
        const path = pathOf(child, root);
        if (path) out.push([...path]);
      }
      walk(child);
    }
  };
  walk(root);
  return out;
}

/**
 * 「位置锁」判据（纯函数，便于单测）：**能不能改动这个元素的位置 / 尺寸 / 旋转**。
 *
 *  · `isLocked` 的元素一律不可动（完全只读：画布上连点都点不中，只能从列表选）
 *  · `positionLocked` 打开时，只有**本次会话由编辑器新建**的元素可以动（`isFresh` 注入判据）
 *    —— 文件里原有的图元位置一律冻结：实验图是严谨生成的，拖一下就废了。
 *
 * 注意「可选中」与「可动」在这一层是**两件事**：位置锁不影响选中，用户仍然点得中、看得见属性。
 */
export function canMoveElement(
  el: Element,
  opts: { positionLocked: boolean; isFresh?: (el: Element) => boolean },
): boolean {
  if (isLocked(el)) return false;
  if (!opts.positionLocked) return true;
  return opts.isFresh?.(el) === true;
}

/**
 * 改 id。
 * 注意：id 可能被 `url(#id)` / `href="#id"` 引用 —— 改名必须**同步改引用**，否则渐变/裁剪会断链。
 * 传空字符串 = 删除 id。
 */
export function renameId(root: Element, els: Iterable<Element>, nextId: string): Command {
  const patches: Patch[] = [];
  const clean = nextId.trim();
  let seq = 0;

  for (const el of els) {
    const path = pathOf(el, root);
    if (!path) continue;
    // 多选批量改名时给第 2 个起加序号后缀：文档里**绝不能出现重复 id**
    const target = clean === '' ? '' : seq === 0 ? clean : `${clean}-${seq + 1}`;
    seq += 1;

    const before = el.getAttribute('id');
    const after = target === '' ? null : target;
    const patch = attrPatch(path, 'id', before, after);
    if (patch) patches.push(patch);

    if (!before || before === target) continue;
    patches.push(...referencePatches(root, before, target));
  }
  return { label: '重命名 id', patches };
}

/** 全文档扫描 url(#old) / href="#old" 引用并改写 */
function referencePatches(root: Element, oldId: string, newId: string): Patch[] {
  const patches: Patch[] = [];
  const nodes = [root, ...Array.from(root.querySelectorAll('*'))];
  for (const node of nodes) {
    const path = pathOf(node, root);
    if (!path) continue;
    for (const attr of Array.from(node.attributes)) {
      const value = attr.value;
      if (!value.includes(`#${oldId}`)) continue;
      const next = value
        .split(`url(#${oldId})`)
        .join(newId === '' ? 'none' : `url(#${newId})`)
        .replace(new RegExp(`^#${oldId}$`), newId === '' ? '' : `#${newId}`);
      if (next !== value) {
        const patch = attrPatch(path, attr.name, value, next);
        if (patch) patches.push(patch);
      }
    }
  }
  return patches;
}
